import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';

import { environment } from '@environments/environment';
import { RequestTimelineService } from '@core/services/request-timeline.service';

/**
 * Feeds the developer "Request Timeline" (rendered by `DebugLoggerComponent`)
 * with real HTTP traffic.
 *
 * For every API request this interceptor records a `pending` entry on the way
 * out and completes it (with status code + duration) once the response or error
 * arrives, via {@link RequestTimelineService}. It never alters the request or
 * response and never swallows errors — it observes the stream and re-emits it
 * unchanged.
 *
 * Tracking is gated on `environment.features.enableDevTools` — the SAME flag
 * that ships the Request Timeline UI. The timeline is rendered by the
 * `test-backend`/`debug-logger` dev route, and `app.routes.ts` only includes
 * that route when `enableDevTools` is true (otherwise it is tree-shaken out).
 * Gating on `enableDevTools` (rather than `enableDebugPanel`) ensures we only
 * incur the per-request bookkeeping where the UI is actually reachable: in
 * production both flags are false (zero overhead), and in staging
 * `enableDevTools` is false (so no wasted work feeding an unreachable panel,
 * even though `enableDebugPanel` is true there).
 */
export const requestTimelineInterceptor: HttpInterceptorFn = (req, next) => {
  // Only trace API traffic, and only where the Request Timeline UI is shipped.
  if (!environment.features.enableDevTools || !req.url.includes('/api/')) {
    return next(req);
  }

  const timeline = inject(RequestTimelineService);
  const id = timeline.addRequest(req.method, req.urlWithParams);
  const startTime = performance.now();

  return next(req).pipe(
    tap({
      next: event => {
        if (event instanceof HttpResponse) {
          const duration = Math.round(performance.now() - startTime);
          timeline.completeRequest(id, event.status, duration);
        }
      },
      error: (error: unknown) => {
        const duration = Math.round(performance.now() - startTime);
        const status = error instanceof HttpErrorResponse ? error.status : 0;
        const message =
          error instanceof HttpErrorResponse
            ? error.message
            : error instanceof Error
              ? error.message
              : 'Request failed';
        timeline.completeRequest(id, status, duration, message);
      }
    })
  );
};
